import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { searchApi } from '../api';
import { displayEntityName, humanizeCode } from '../lib/auditLabels';
import { IconClose, IconOrgs, IconSearch, IconUsers } from './Icons';
import { Avatar } from './BackroomChrome';

export function CommandPalette({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selected, setSelected] = useState(0);

  const q = query.trim();
  const debounced = debouncedQuery.trim();

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedQuery(query), 250);
    return () => window.clearTimeout(handle);
  }, [query]);

  const searchQ = useQuery({
    queryKey: ['admin-search', debounced],
    queryFn: () => searchApi.global(debounced),
    enabled: debounced.length >= 2,
  });

  const orgs = searchQ.data?.data.organizations ?? [];
  const users = searchQ.data?.data.users ?? [];
  const operational = searchQ.data?.data.operational ?? [];
  const results = [
    ...orgs.map((o) => ({ type: 'org' as const, id: o.id, href: `/orgs/${o.id}`, label: o.name, sub: `${o.status} · ${o.memberCount} members` })),
    ...users.map((u) => ({ type: 'user' as const, id: u.id, href: `/users/${u.id}`, label: u.displayName, sub: u.email })),
    ...operational.map((o) => ({
      type: 'ops' as const,
      id: o.id,
      href: o.href,
      label: o.label,
      sub: `${humanizeCode(o.type)} · ${humanizeCode(o.status)}${o.organizationName ? ` · ${o.organizationName}` : ''}`,
    })),
  ];

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelected((s) => Math.min(s + 1, Math.max(results.length - 1, 0)));
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelected((s) => Math.max(s - 1, 0));
      }
      if (e.key === 'Enter') {
        const r = results[selected];
        if (r) {
          navigate(r.href);
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  return (
    <div className="cmd-overlay" onClick={onClose} role="dialog" aria-label="Search">
      <div className="cmd-palette" onClick={(e) => e.stopPropagation()}>
        <div className="cmd-palette__head">
          <IconSearch className="icon-15 text-muted icon-shrink-0" />
          <input
            autoFocus
            className="cmd-palette__input"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setSelected(0); }}
            placeholder="Search orgs, users, event/delivery/import IDs…"
          />
          <button type="button" className="icon-btn-close" onClick={onClose}>
            <IconClose className="icon-14" />
          </button>
        </div>
        <div className="cmd-palette__results">
          {q.length < 2 ? (
            <div className="br-empty"><div className="br-empty__body">Type at least 2 characters to search.</div></div>
          ) : q !== debounced || searchQ.isFetching ? (
            <div className="br-empty"><div className="br-empty__body">Searching…</div></div>
          ) : results.length === 0 ? (
            <div className="br-empty"><div className="br-empty__title">No results for “{query}”</div></div>
          ) : (
            <>
              {orgs.length > 0 && (
                <div>
                  <div className="sep-label cmd-palette__group-label">
                    <IconOrgs className="icon-12" /> Organizations
                  </div>
                  {orgs.map((o, i) => (
                    <button
                      key={o.id}
                      type="button"
                      className={`nav-item cmd-result${selected === i ? ' is-selected' : ''}`}
                      onClick={() => { navigate(`/orgs/${o.id}`); onClose(); }}
                    >
                      <Avatar name={o.name} size={28} square />
                      <div>
                        <div className="cmd-result__title">{displayEntityName(o.name, 'Organization')}</div>
                        <div className="cmd-result__sub">{humanizeCode(o.status)}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {users.length > 0 && (
                <div>
                  <div className="sep-label cmd-palette__group-label">
                    <IconUsers className="icon-12" /> Users
                  </div>
                  {users.map((u, i) => {
                    const idx = orgs.length + i;
                    return (
                      <button
                        key={u.id}
                        type="button"
                        className={`nav-item cmd-result${selected === idx ? ' is-selected' : ''}`}
                        onClick={() => { navigate(`/users/${u.id}`); onClose(); }}
                      >
                        <Avatar name={u.displayName} size={28} />
                        <div>
                          <div className="cmd-result__title">{displayEntityName(u.displayName, 'User')}</div>
                          <div className="cmd-result__sub">{u.email}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
              {operational.length > 0 && (
                <div>
                  <div className="sep-label cmd-palette__group-label">
                    <IconSearch className="icon-12" /> Operational
                  </div>
                  {operational.map((o, i) => {
                    const idx = orgs.length + users.length + i;
                    return (
                      <button
                        key={`${o.type}-${o.id}`}
                        type="button"
                        className={`nav-item cmd-result${selected === idx ? ' is-selected' : ''}`}
                        onClick={() => { navigate(o.href); onClose(); }}
                      >
                        <div>
                          <div className="cmd-result__title">{o.label}</div>
                          <div className="cmd-result__sub">
                            {humanizeCode(o.type)} · {humanizeCode(o.status)}
                            {o.organizationName ? ` · ${displayEntityName(o.organizationName, 'Organization')}` : ''}
                          </div>
                          <div className="cmd-result__sub">{o.summary}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
        <div className="cmd-palette__foot">
          <span><kbd>↑↓</kbd> navigate</span>
          <span><kbd>↵</kbd> open</span>
          <span><kbd>Esc</kbd> close</span>
        </div>
      </div>
    </div>
  );
}
